/**
 * Guided appearance description — option lists (value + i18n label key +,
 * where it helps on a phone, a colour swatch) and the pure validation used
 * by `src/pages/ChildAppearance.tsx`. English values match the backend's
 * `child_sheet.AppearanceIn` exactly (docs/plans/2026-09-27-chouette-
 * planche-enfant.md §3); only the *labels* are French, via i18n.
 *
 * No photo anywhere: every field here is a guided choice from a short list,
 * except `companion_detail` (free text, normalised and possibly refused
 * server-side — see `api.putChildAppearance`).
 */
import type {
  AppearanceInput,
  CompanionColor,
  CompanionKind,
  HairColor,
  HairLength,
  HairTexture,
  Hairstyle,
  PresentedAs,
  PyjamaColor,
  PyjamaPattern,
  Skin,
} from "./types";

export const COMPANION_DETAIL_MAX_LENGTH = 150;
export const CHILD_DETAIL_MAX_LENGTH = 150;
export const COMPANION_KIND_OTHER_MAX_LENGTH = 40;

interface Option<T extends string> {
  value: T;
  labelKey: string;
  /** CSS colour for a swatch chip — only set on colour-like fields. */
  swatch?: string;
}

export const PRESENTED_AS_OPTIONS: Option<PresentedAs>[] = [
  { value: "girl", labelKey: "appearance.presentedAs.girl" },
  { value: "boy", labelKey: "appearance.presentedAs.boy" },
  { value: "unspecified", labelKey: "appearance.presentedAs.unspecified" },
];

export const SKIN_OPTIONS: Option<Skin>[] = [
  { value: "very_fair", labelKey: "appearance.skin.very_fair", swatch: "#f6e0cf" },
  { value: "fair", labelKey: "appearance.skin.fair", swatch: "#eec9a5" },
  { value: "olive", labelKey: "appearance.skin.olive", swatch: "#d8ae7c" },
  { value: "brown", labelKey: "appearance.skin.brown", swatch: "#a9754e" },
  { value: "dark", labelKey: "appearance.skin.dark", swatch: "#6b4530" },
];

export const HAIR_COLOR_OPTIONS: Option<HairColor>[] = [
  { value: "blond", labelKey: "appearance.hairColor.blond", swatch: "#e8c777" },
  { value: "light_brown", labelKey: "appearance.hairColor.light_brown", swatch: "#a9764f" },
  { value: "chestnut", labelKey: "appearance.hairColor.chestnut", swatch: "#7a4a2b" },
  { value: "dark_brown", labelKey: "appearance.hairColor.dark_brown", swatch: "#4a2e1c" },
  { value: "black", labelKey: "appearance.hairColor.black", swatch: "#241a16" },
  { value: "ginger", labelKey: "appearance.hairColor.ginger", swatch: "#c9622c" },
];

export const HAIR_LENGTH_OPTIONS: Option<HairLength>[] = [
  { value: "very_short", labelKey: "appearance.hairLength.very_short" },
  { value: "short", labelKey: "appearance.hairLength.short" },
  { value: "shoulder", labelKey: "appearance.hairLength.shoulder" },
  { value: "long", labelKey: "appearance.hairLength.long" },
];

export const HAIR_TEXTURE_OPTIONS: Option<HairTexture>[] = [
  { value: "straight", labelKey: "appearance.hairTexture.straight" },
  { value: "wavy", labelKey: "appearance.hairTexture.wavy" },
  { value: "curly", labelKey: "appearance.hairTexture.curly" },
  { value: "coily", labelKey: "appearance.hairTexture.coily" },
];

export const HAIRSTYLE_OPTIONS: Option<Hairstyle>[] = [
  { value: "loose", labelKey: "appearance.hairstyle.loose" },
  { value: "bunches", labelKey: "appearance.hairstyle.bunches" },
  { value: "ponytail", labelKey: "appearance.hairstyle.ponytail" },
  { value: "braids", labelKey: "appearance.hairstyle.braids" },
  { value: "bun", labelKey: "appearance.hairstyle.bun" },
  { value: "afro", labelKey: "appearance.hairstyle.afro" },
];

export const PYJAMA_COLOR_OPTIONS: Option<PyjamaColor>[] = [
  { value: "yellow", labelKey: "appearance.pyjamaColor.yellow", swatch: "#ffce5c" },
  { value: "coral", labelKey: "appearance.pyjamaColor.coral", swatch: "#ff7a6b" },
  { value: "mint", labelKey: "appearance.pyjamaColor.mint", swatch: "#7fd8c0" },
  { value: "lilac", labelKey: "appearance.pyjamaColor.lilac", swatch: "#a78bfa" },
  { value: "cream", labelKey: "appearance.pyjamaColor.cream", swatch: "#f4efe6" },
];

export const PYJAMA_PATTERN_OPTIONS: Option<PyjamaPattern>[] = [
  { value: "plain", labelKey: "appearance.pyjamaPattern.plain" },
  { value: "stars", labelKey: "appearance.pyjamaPattern.stars" },
  { value: "dots", labelKey: "appearance.pyjamaPattern.dots" },
  { value: "stripes", labelKey: "appearance.pyjamaPattern.stripes" },
];

export const COMPANION_KIND_OPTIONS: Option<CompanionKind>[] = [
  { value: "rabbit", labelKey: "appearance.companionKind.rabbit" },
  { value: "bear", labelKey: "appearance.companionKind.bear" },
  { value: "dog", labelKey: "appearance.companionKind.dog" },
  { value: "cat", labelKey: "appearance.companionKind.cat" },
  { value: "owl", labelKey: "appearance.companionKind.owl" },
  { value: "elephant", labelKey: "appearance.companionKind.elephant" },
  { value: "sheep", labelKey: "appearance.companionKind.sheep" },
  { value: "dinosaur", labelKey: "appearance.companionKind.dinosaur" },
  { value: "monkey", labelKey: "appearance.companionKind.monkey" },
  { value: "fox", labelKey: "appearance.companionKind.fox" },
  { value: "lion", labelKey: "appearance.companionKind.lion" },
  { value: "giraffe", labelKey: "appearance.companionKind.giraffe" },
  { value: "penguin", labelKey: "appearance.companionKind.penguin" },
  { value: "unicorn", labelKey: "appearance.companionKind.unicorn" },
  { value: "blanket", labelKey: "appearance.companionKind.blanket" },
  { value: "other", labelKey: "appearance.companionKind.other" },
];

export const COMPANION_COLOR_OPTIONS: Option<CompanionColor>[] = [
  { value: "cream", labelKey: "appearance.companionColor.cream", swatch: "#f4efe6" },
  { value: "grey", labelKey: "appearance.companionColor.grey", swatch: "#a9a4bd" },
  { value: "brown", labelKey: "appearance.companionColor.brown", swatch: "#8a5a37" },
  { value: "pink", labelKey: "appearance.companionColor.pink", swatch: "#f2a6c2" },
  { value: "blue", labelKey: "appearance.companionColor.blue", swatch: "#7fb8e0" },
  { value: "yellow", labelKey: "appearance.companionColor.yellow", swatch: "#ffce5c" },
  { value: "white", labelKey: "appearance.companionColor.white", swatch: "#f8f8f8" },
];

/** Fields the parent must actively choose — no default worth guessing at.
 *  `presented_as`, `glasses` and `pyjama_pattern` all have sane defaults
 *  ("unspecified" / false / "plain") and are never required. */
export type RequiredAppearanceField =
  | "skin" | "hair_color" | "hair_length" | "hair_texture" | "hairstyle"
  | "pyjama_color" | "companion_kind" | "companion_color";

export const REQUIRED_APPEARANCE_FIELDS: RequiredAppearanceField[] = [
  "skin", "hair_color", "hair_length", "hair_texture", "hairstyle",
  "pyjama_color", "companion_kind", "companion_color",
];

export type DraftAppearance = Partial<AppearanceInput>;

/** Pure guard: true once every required field has been chosen, the free-text
 *  fields (if any) fit the server's limits, and — when the doudou's type is
 *  "other" — a non-empty free type has been given (max 40 chars). Used to
 *  enable the save button and to decide whether a freshly-created child
 *  needs the "Plus tard" offer at all. */
export function isAppearanceComplete(draft: DraftAppearance): draft is AppearanceInput {
  const companionDetail = draft.companion_detail ?? "";
  const childDetail = draft.child_detail ?? "";
  const kindOther = (draft.companion_kind_other ?? "").trim();
  return (
    REQUIRED_APPEARANCE_FIELDS.every((field) => draft[field] !== undefined) &&
    companionDetail.length <= COMPANION_DETAIL_MAX_LENGTH &&
    childDetail.length <= CHILD_DETAIL_MAX_LENGTH &&
    (draft.companion_kind !== "other" ||
      (kindOther.length > 0 && kindOther.length <= COMPANION_KIND_OTHER_MAX_LENGTH))
  );
}

/** Guards against a stray paste blowing past the server's 150-char limit
 *  before the request is even sent (the server would 422 on the generic
 *  Pydantic constraint, not the friendlier `companion_detail_refused`). */
export function clampCompanionDetail(text: string): string {
  return text.slice(0, COMPANION_DETAIL_MAX_LENGTH);
}

/** Same guard as `clampCompanionDetail`, for the child's own free detail. */
export function clampChildDetail(text: string): string {
  return text.slice(0, CHILD_DETAIL_MAX_LENGTH);
}

/** Same guard, for the doudou's free-text type ("Autre"), max 40 chars. */
export function clampCompanionKindOther(text: string): string {
  return text.slice(0, COMPANION_KIND_OTHER_MAX_LENGTH);
}

export const DEFAULT_APPEARANCE_DRAFT: DraftAppearance = {
  presented_as: "unspecified",
  glasses: false,
  pyjama_pattern: "plain",
  companion_detail: "",
  companion_kind_other: "",
  child_detail: "",
};
